import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { get, post } from '../../services/api';
import styles from './TimetableHistory.module.css';

const TimetableHistory = ({ configurationId, headers }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [configurationId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await get(
        `Timetable/GetHistory?configurationId=${configurationId}`,
        { headers }
      );
      setHistory(response || []);
    } catch (error) {
      console.error("Error loading history:", error);
      toast.error("Failed to load timetable history");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (historyId) => {
    if (!window.confirm("Are you sure you want to restore this timetable? This will replace the current timetable.")) {
      return;
    }

    try {
      setLoading(true);
      await post(
        `Timetable/RestoreFromHistory?historyId=${historyId}`,
        {},
        { headers }
      );
      toast.success("Timetable restored successfully");
      await loadHistory();
    } catch (error) {
      console.error("Error restoring timetable:", error);
      toast.error("Failed to restore timetable");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewSnapshot = (historyItem) => {
    setSelectedHistory(historyItem);
  };

  const closeModal = () => {
    setSelectedHistory(null);
  };

  if (loading && history.length === 0) {
    return <div className={styles.loading}>Loading history...</div>;
  }

  return (
    <div className={styles.historyContainer}>
      <h3>Timetable History</h3>

      {history.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No timetable history available yet.</p>
        </div>
      ) : (
        <div className={styles.historyList}>
          {history.map((item) => (
            <div key={item.id} className={styles.historyItem}>
              <div className={styles.historyInfo}>
                <div className={styles.historyDate}>
                  {formatDate(item.snapshotDate)}
                </div>
                <div className={styles.historyReason}>
                  <strong>Reason:</strong> {item.reason || 'No reason provided'}
                </div>
                {item.modifiedBy && (
                  <div className={styles.historyModifier}>
                    <strong>Modified by:</strong> {item.modifiedBy}
                  </div>
                )}
              </div>
              <div className={styles.historyActions}>
                <button
                  className={styles.viewButton}
                  onClick={() => viewSnapshot(item)}
                >
                  View Details
                </button>
                <button
                  className={styles.restoreButton}
                  onClick={() => handleRestore(item.id)}
                  disabled={loading}
                >
                  Restore
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedHistory && (
        <div className={styles.modal} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4>Snapshot Details</h4>
              <button className={styles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.snapshotInfo}>
                <p><strong>Date:</strong> {formatDate(selectedHistory.snapshotDate)}</p>
                <p><strong>Reason:</strong> {selectedHistory.reason}</p>
                {selectedHistory.modifiedBy && (
                  <p><strong>Modified by:</strong> {selectedHistory.modifiedBy}</p>
                )}
              </div>
              <div className={styles.snapshotData}>
                <h5>Snapshot Data:</h5>
                <pre>{JSON.stringify(JSON.parse(selectedHistory.snapshotData), null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimetableHistory;